/**
 * Seed the composer's two stores (create-flow + composer draft) from an
 * existing post (edit mode, `?post=<id>`) or a caption template ("Use").
 *
 * The composer lazy-initialises its local state from these stores on mount,
 * so seeding BEFORE the composer form mounts is all edit mode needs: caption,
 * hashtags, media (with kinds + alt text), the targets and their per-network
 * captions (`post_platforms.adapted_content`), the schedule (as wall time in
 * the browser's zone) and every publish option stored on `posts.metadata`.
 *
 * @module lib/composerSeed
 */

import type { PostPlatformStatus } from "../api/postApi";
import { useComposerDraftStore } from "../stores/composerDraftStore";
import { useCreateFlowStore } from "../stores/useCreateFlowStore";
import type { Account, AttachedMedia, Platform, Post, Template } from "../types/social";
import { isVideoUrl } from "../types/social";
import {
  DEFAULT_INSTAGRAM_AUDIO_VOLUME,
  DEFAULT_INSTAGRAM_VIDEO_VOLUME,
  DEFAULT_PLATFORM_OPTIONS,
  type GbpCtaType,
  type GbpTopicType,
  type InstagramAudioType,
  type PlatformOptionsState,
  type ThreadsReplyControl,
  type TikTokPrivacy,
} from "./platformOptions";
import { metadataPlatformCaptions } from "./previewTargets";
import { browserTimezone, isValidTimezone, wallTimeInZone } from "./scheduling";

/** Target rows that can still be (re)sent — a published one never re-publishes. */
const EDITABLE_TARGET_STATUSES = new Set(["draft", "scheduled", "failed", "cancelled", "pending_approval", ""]);

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && Boolean(x.trim())) : [];
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * The per-platform option state stored on `posts.metadata` (the inverse of
 * lib/platformOptions `buildPlatformOptionsMetadata`) — edit mode re-opens a
 * post with its sponsorship / IG story / trial reel / collaborators / Threads
 * / TikTok / GBP choices intact, so saving never silently clears them.
 */
export function platformOptionsFromMetadata(raw: unknown): PlatformOptionsState {
  const m = obj(raw);
  const s: PlatformOptionsState = { ...DEFAULT_PLATFORM_OPTIONS, threadsPollOptions: ["", ""] };
  s.sponsored = m.sponsored === true;
  s.igSponsorIds = strList(m.ig_sponsor_ids).join(", ");
  s.facebookFormat = m.facebook_format === "story" ? "story" : "feed";
  s.instagramFormat = m.instagram_format === "story" ? "story" : "feed";
  s.instagramTrialReel = m.instagram_trial_reel === true;
  if (m.instagram_trial_graduation === "SS_PERFORMANCE") s.instagramTrialGraduation = "SS_PERFORMANCE";
  s.instagramCollaborators = strList(m.instagram_collaborators).join(", ");
  const audio = obj(m.instagram_audio);
  const audioId = str(audio.audio_id);
  if (audioId && /^\d{1,25}$/.test(audioId)) {
    s.instagramAudio = {
      audioId,
      audioType: (audio.audio_type === "original_sound" ? "original_sound" : "music") as InstagramAudioType,
      title: str(audio.title) ?? "",
      artist: str(audio.artist) ?? "",
      audioVolume:
        typeof audio.audio_volume === "number"
          ? Math.min(1, Math.max(0, audio.audio_volume))
          : DEFAULT_INSTAGRAM_AUDIO_VOLUME,
      videoVolume:
        typeof audio.video_volume === "number"
          ? Math.min(1, Math.max(0, audio.video_volume))
          : DEFAULT_INSTAGRAM_VIDEO_VOLUME,
      mixOverBakedMusic: audio.mix_over_baked_music === true,
    };
  }
  s.threadsTopicTag = str(m.threads_topic_tag) ?? "";
  const poll = strList(m.threads_poll_options);
  if (poll.length >= 2) {
    s.threadsPollEnabled = true;
    s.threadsPollOptions = poll;
  }
  if (typeof m.threads_reply_control === "string")
    s.threadsReplyControl = m.threads_reply_control as ThreadsReplyControl;
  if (typeof m.tiktok_privacy_level === "string") s.tiktokPrivacy = m.tiktok_privacy_level as TikTokPrivacy;
  // Stored as "disable_*"; a post without them keeps TikTok's off-by-default.
  if (typeof m.tiktok_disable_comment === "boolean") s.tiktokAllowComment = !m.tiktok_disable_comment;
  if (typeof m.tiktok_disable_duet === "boolean") s.tiktokAllowDuet = !m.tiktok_disable_duet;
  if (typeof m.tiktok_disable_stitch === "boolean") s.tiktokAllowStitch = !m.tiktok_disable_stitch;
  s.tiktokYourBrand = m.tiktok_brand_organic === true;
  s.tiktokBrandedContent = m.tiktok_brand_content === true;
  s.tiktokDisclose = s.tiktokYourBrand || s.tiktokBrandedContent;
  if (m.gbp_topic_type === "EVENT" || m.gbp_topic_type === "OFFER") s.gbpTopicType = m.gbp_topic_type as GbpTopicType;
  const event = obj(m.gbp_event);
  s.gbpEventTitle = str(event.title) ?? "";
  s.gbpStartDate = str(event.start_date) ?? "";
  s.gbpStartTime = str(event.start_time) ?? "";
  s.gbpEndDate = str(event.end_date) ?? "";
  s.gbpEndTime = str(event.end_time) ?? "";
  const offer = obj(m.gbp_offer);
  s.gbpCouponCode = str(offer.coupon_code) ?? "";
  s.gbpRedeemUrl = str(offer.redeem_url) ?? "";
  s.gbpTerms = str(offer.terms) ?? "";
  if (typeof m.gbp_cta_type === "string") s.gbpCtaType = m.gbp_cta_type as GbpCtaType;
  return s;
}

/** Media of a post with its kind (declared media_types, else inferred from the URL). */
export function postMedia(post: Pick<Post, "media_urls" | "media_types">): AttachedMedia[] {
  return (post.media_urls ?? []).map((url, i) => ({
    url,
    type: post.media_types?.[i] === "video" || isVideoUrl(url) ? ("video" as const) : ("image" as const),
  }));
}

/** The unpublished targets of a post (platform + account), first per platform wins. */
export function editableTargets(rows: PostPlatformStatus[]): Array<{ platform: string; account_id: string }> {
  const seen = new Set<string>();
  const out: Array<{ platform: string; account_id: string }> = [];
  for (const r of rows) {
    if (!r.account_id || !EDITABLE_TARGET_STATUSES.has(r.status ?? "") || seen.has(String(r.platform))) continue;
    seen.add(String(r.platform));
    out.push({ platform: String(r.platform), account_id: r.account_id });
  }
  return out;
}

/** Load `post` (+ its per-network targets) into the composer stores for editing. */
export function seedComposerFromPost(post: Post, rows: PostPlatformStatus[]): void {
  const flow = useCreateFlowStore.getState();
  flow.reset();
  const meta = (post.metadata ?? {}) as Record<string, unknown>;
  const media = postMedia(post);
  flow.setCaption(post.content ?? "");
  flow.setHashtags(post.hashtags ?? []);
  // Migration 083, lane F5 2026-10-06 — Post.tags isn't declared on the
  // shared Post type yet (lane S5 owns types/social.ts this session); read it
  // defensively so this seed never breaks if the field is absent.
  flow.setTags(strList((post as Post & { tags?: unknown }).tags));
  flow.setMediaUrls(media);
  flow.setPlatforms(editableTargets(rows));
  const designId = str(meta.design_id) ?? post.linked_design_id ?? null;
  if (designId) useCreateFlowStore.setState({ designId });
  const genId = str(meta.copywriter_generation_id);
  if (genId) {
    useCreateFlowStore.setState({
      generationId: genId,
      generationVariantIndex: typeof meta.copywriter_variant_index === "number" ? meta.copywriter_variant_index : null,
    });
  }
  const ideaId = str(meta.content_idea_id);
  if (ideaId) flow.setContentIdea(ideaId);

  // A real schedule wins; otherwise the post's PROPOSED publish time
  // (migration 087) re-opens in the zone it was proposed in.
  const proposedTz = post.proposed_timezone && isValidTimezone(post.proposed_timezone) ? post.proposed_timezone : null;
  const seedAt = post.scheduled_at ?? post.proposed_publish_at ?? null;
  const tz = post.scheduled_at ? browserTimezone() : (proposedTz ?? browserTimezone());
  if (seedAt) {
    const wall = wallTimeInZone(new Date(seedAt), tz);
    flow.setScheduledAt(`${wall.date}T${wall.time}`);
  }
  flow.setPost(post.id);

  const draft = useComposerDraftStore.getState();
  draft.reset();
  // A target-less draft (only proposed networks) keeps its per-network
  // captions in metadata.platform_captions (Autopilot drafts, and PUT
  // /api/posts/{id} since 2026-10-14). Seed those first so saving the post
  // sends them back instead of clearing them; a target row's own
  // adapted_content wins.
  const overrides: Partial<Record<Platform, string>> = {};
  for (const [platform, text] of Object.entries(metadataPlatformCaptions(meta))) {
    if (text.trim()) overrides[platform as Platform] = text;
  }
  for (const r of rows) {
    if (r.adapted_content && EDITABLE_TARGET_STATUSES.has(r.status ?? "")) {
      overrides[r.platform as Platform] = r.adapted_content;
    }
  }
  const alts = Array.isArray(meta.media_alt_text) ? (meta.media_alt_text as unknown[]) : [];
  const altText: Record<string, string> = {};
  media.forEach((m, i) => {
    const alt = alts[i];
    if (typeof alt === "string" && alt) altText[m.url] = alt;
  });
  const offset = meta.video_thumb_offset_ms;
  useComposerDraftStore.setState({
    overrides,
    altText,
    firstComment: post.first_comment ?? "",
    linkUrl: post.link_url ?? "",
    postTitle: str(meta.title) ?? "",
    // The schedule above is wall time in this zone.
    timezone: seedAt ? tz : null,
    tiktokPublishMode: meta.tiktok_publish_mode === "direct" ? "direct" : "draft",
    pinterestBoardId: str(meta.pinterest_board_id) ?? "",
    facebookVideoFormat: meta.facebook_video_format === "reel" ? "reel" : "feed",
    videoTitle: str(meta.video_title) ?? "",
    instagramShareToFeed: meta.instagram_share_to_feed !== false,
    videoCoverUrl: str(meta.video_cover_url) ?? "",
    videoCoverSeconds: typeof offset === "number" ? offset / 1000 : null,
    utmEnabled: meta.utm_enabled === true,
    utmCampaign: str(meta.utm_campaign) ?? "",
    // Per-platform options re-open as stored (saving never silently clears them).
    platformOptions: platformOptionsFromMetadata(meta),
    // Edit mode never writes the "restore unsaved draft" snapshot.
    snapshot: null,
    savedAt: null,
  });
}

/**
 * Start a NEW post from a caption template: copy, hashtags, media, the first
 * comment / link it carries, and its networks (the first active account of
 * each — the composer lets the user switch accounts).
 */
export function seedComposerFromTemplate(tpl: Template, accounts: Account[] = []): void {
  const flow = useCreateFlowStore.getState();
  flow.reset();
  flow.setCaption(tpl.content ?? "");
  flow.setHashtags((tpl.hashtags ?? []).map((t) => (t.startsWith("#") ? t : `#${t}`)));
  flow.setMediaUrls(postMedia({ media_urls: tpl.media_urls ?? [], media_types: [] }));
  const targets: Array<{ platform: string; account_id: string }> = [];
  for (const p of tpl.platforms ?? []) {
    const acc = accounts.find((a) => a.platform === p && a.status === "active");
    if (acc) targets.push({ platform: p, account_id: acc.id });
  }
  flow.setPlatforms(targets);
  const draft = useComposerDraftStore.getState();
  draft.reset();
  const meta = (tpl.metadata ?? {}) as Record<string, unknown>;
  useComposerDraftStore.setState({
    firstComment: str(meta.first_comment) ?? "",
    linkUrl: str(meta.link_url) ?? "",
    snapshot: null,
    savedAt: null,
  });
}
